import { i18n } from "../../translate/i18n";
import React, { useState, useEffect, useContext } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Button, TextField, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { IconButton, InputAdornment } from "@mui/material";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(() => ({
  root: {
    display: "flex",
    width: "100vw",
    minHeight: "100vh",
    background: "linear-gradient(160deg, #1a1040 0%, #1e1550 50%, #160e3a 100%)",
    justifyContent: "center",
    alignItems: "center",
    padding: "20px",
  },

  card: {
    width: "100%",
    maxWidth: "400px",
    background: "linear-gradient(160deg, #221d52 0%, #1c1945 60%, #201c50 100%)",
    borderRadius: "18px",
    border: "1px solid rgba(230,200,80,.85)",
    boxShadow: "0 0 0 1px rgba(230,200,80,.18), 0 24px 60px rgba(0,0,0,.6)",
    padding: "36px 32px 30px",
    opacity: 0,
    transform: "translateY(16px)",
    animation: "$fadeUp .45s ease forwards",
  },

  logoRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    marginBottom: "6px",
  },
  logoBolt: {
    width: "38px",
    height: "38px",
    background: "linear-gradient(135deg, #5c2db8 0%, #7c4ee8 100%)",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 4px 16px rgba(100,60,220,.45)",
    flexShrink: 0,
  },
  logoText: {
    fontSize: "24px",
    fontWeight: 700,
    letterSpacing: "-0.02em",
    background: "linear-gradient(135deg, #e8c840 0%, #d4af37 60%, #f0d060 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    fontFamily: "system-ui, -apple-system, sans-serif",
  },

  title: {
    textAlign: "center",
    fontSize: "17px",
    fontWeight: 600,
    color: "#d4c8f0",
    margin: "0 0 3px",
    fontFamily: "system-ui, -apple-system, sans-serif",
    letterSpacing: "0.01em",
  },
  subtitle: {
    textAlign: "center",
    fontSize: "13px",
    color: "rgba(160,150,200,.6)",
    margin: "0 0 24px",
    fontFamily: "system-ui, -apple-system, sans-serif",
  },

  divider: {
    height: "1px",
    background: "linear-gradient(90deg, transparent, rgba(230,200,80,.6), transparent)",
    marginBottom: "24px",
  },

  errorBox: {
    fontSize: "12px",
    color: "#fca5a5",
    background: "rgba(239,68,68,.08)",
    border: "1px solid rgba(239,68,68,.2)",
    borderRadius: "8px",
    padding: "8px 12px",
    marginBottom: "14px",
    textAlign: "center",
    fontFamily: "system-ui, -apple-system, sans-serif",
  },

  label: {
    display: "block",
    fontSize: "11px",
    fontWeight: 600,
    color: "rgba(212,175,55,.8)",
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    marginBottom: "6px",
    fontFamily: "system-ui, -apple-system, sans-serif",
  },
  fieldWrap: {
    position: "relative",
    marginBottom: "16px",
  },
  iconLeft: {
    position: "absolute",
    left: "13px",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 2,
    pointerEvents: "none",
    color: "rgba(180,160,230,.5)",
    display: "flex",
    alignItems: "center",
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      backgroundColor: "rgba(8,6,28,.6)",
      "& fieldset": {
        borderColor: "rgba(130,100,220,.3)",
        transition: "border-color 200ms",
      },
      "&:hover fieldset": { borderColor: "rgba(180,140,240,.55)" },
      "&.Mui-focused fieldset": {
        borderColor: "rgba(230,200,80,.9)",
        borderWidth: "1px",
        boxShadow: "0 0 0 3px rgba(230,200,80,.12)",
      },
      "& input": {
        color: "#d8d0f0",
        fontFamily: "system-ui, -apple-system, sans-serif",
        fontSize: "14px",
        paddingLeft: "40px",
        paddingTop: "12px",
        paddingBottom: "12px",
      },
      "& input::placeholder": {
        color: "rgba(120,110,170,.4)",
        fontStyle: "italic",
        opacity: 1,
      },
      "& input:-webkit-autofill": {
        WebkitBoxShadow: "0 0 0 100px rgba(8,6,28,.99) inset !important",
        WebkitTextFillColor: "#d8d0f0 !important",
      },
    },
  },

  rememberRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "4px",
    cursor: "pointer",
  },
  toggle: {
    width: "36px",
    height: "20px",
    borderRadius: "10px",
    background: "rgba(100,80,180,.2)",
    border: "1px solid rgba(130,100,220,.35)",
    position: "relative",
    flexShrink: 0,
    transition: "background .25s, border-color .25s",
  },
  toggleOn: {
    background: "linear-gradient(90deg, #7c4ee8, #9b6ff5)",
    borderColor: "rgba(160,120,255,.6) !important",
  },
  toggleThumb: {
    position: "absolute",
    top: "3px",
    left: "3px",
    width: "12px",
    height: "12px",
    borderRadius: "50%",
    background: "rgba(180,160,230,.6)",
    transition: "transform .25s, background .25s",
  },
  toggleThumbOn: {
    transform: "translateX(16px)",
    background: "#fff",
  },
  rememberLabel: {
    fontSize: "13px",
    color: "rgba(180,160,220,.65)",
    fontFamily: "system-ui, -apple-system, sans-serif",
    userSelect: "none",
  },

  submitBtn: {
    marginTop: "20px",
    width: "100%",
    background: "linear-gradient(135deg, #4a28b0 0%, #6a3ee0 50%, #5030c8 100%) !important",
    color: "#f0e0a0 !important",
    borderRadius: "10px !important",
    padding: "13px !important",
    fontWeight: "700 !important",
    fontSize: "14px !important",
    letterSpacing: "0.1em !important",
    textTransform: "uppercase !important",
    fontFamily: "system-ui, -apple-system, sans-serif !important",
    border: "1px solid rgba(230,200,80,.75) !important",
    boxShadow: "0 4px 20px rgba(80,40,200,.4) !important",
    transition: "transform 150ms, box-shadow 150ms !important",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 6px 26px rgba(80,40,200,.55) !important",
    },
    "&:active": { transform: "translateY(0)" },
  },

  links: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "10px",
    marginTop: "18px",
  },
  linkForgot: {
    fontSize: "13px",
    color: "rgba(180,160,230,.6)",
    textDecoration: "none",
    fontFamily: "system-ui, -apple-system, sans-serif",
    transition: "color 150ms",
    "&:hover": { color: "#d4af37" },
  },
  sep: {
    fontSize: "11px",
    color: "rgba(130,110,180,.35)",
    fontFamily: "system-ui, -apple-system, sans-serif",
  },
  linkCreate: {
    fontSize: "13px",
    fontWeight: 600,
    color: "rgba(212,175,55,.8)",
    textDecoration: "none",
    border: "1px solid rgba(230,200,80,.65)",
    padding: "6px 20px",
    borderRadius: "20px",
    fontFamily: "system-ui, -apple-system, sans-serif",
    transition: "all 150ms",
    "&:hover": { background: "rgba(212,175,55,.08)", color: "#e8c840" },
  },

  footer: {
    marginTop: "20px",
    fontSize: "10px",
    color: "rgba(130,110,180,.35)",
    textAlign: "center",
    fontFamily: "system-ui, -apple-system, sans-serif",
    letterSpacing: "0.03em",
  },

  whatsappBtn: {
    position: "fixed",
    bottom: "22px",
    right: "22px",
    width: "48px",
    height: "48px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, #c9a227 0%, #e8c840 100%)",
    boxShadow: "0 4px 16px rgba(200,160,30,.45)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    zIndex: 999,
    transition: "transform 150ms",
    "&:hover": { transform: "scale(1.08)" },
  },

  "@keyframes fadeUp": {
    to: { opacity: 1, transform: "translateY(0)" },
  },
}));

const Login = () => {
  const classes = useStyles();
  const { handleLogin } = useContext(AuthContext);

  const [user, setUser] = useState({ email: "", password: "", remember: false });
  const [branding, setBranding] = useState({
    loginLogo: "/logo.png",
    loginBackground: "",
    loginWhatsapp: "https://wa.me/5511911797438",
  });
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [userCreationEnabled, setUserCreationEnabled] = useState(true);

  useEffect(() => {
    try { localStorage.setItem("theme", "light"); } catch (e) {}
    document.documentElement.classList.remove("dark");
    document.documentElement.setAttribute("data-theme", "light");
    document.body.classList.add("login-page");
    return () => document.body.classList.remove("login-page");
  }, []);

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const { data } = await api.get("/global-config/public-branding");
        setBranding({
          loginLogo: data.loginLogo || "/logo.png",
          loginBackground: data.loginBackground || "",
          loginWhatsapp: data.loginWhatsapp || "https://wa.me/5511911797438",
        });
      } catch (err) {}
    };
    fetchBranding();
  }, []);

  useEffect(() => {
    const fetchUserCreationStatus = async () => {
      try {
        const { data } = await api.get("/settings/userCreation");
        setUserCreationEnabled(data.userCreation === "enabled");
      } catch (err) {
        setUserCreationEnabled(false);
      }
    };
    fetchUserCreationStatus();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const lang = localStorage.getItem("i18nextLng") || "pt";
    i18n.changeLanguage(lang);
    handleLogin(user);
  };

  return (
    <div className={classes.root}>
      <div className={classes.card}>

        {/* Logo */}
        <div className={classes.logoRow}>
          <div className={classes.logoBolt}>
            <svg width="18" height="22" viewBox="0 0 20 24" fill="none">
              <path d="M12 2L3 13h7l-2 9 10-13h-7l2-7z" fill="white" />
            </svg>
          </div>
          <span className={classes.logoText}>ChatPrime</span>
        </div>

        <p className={classes.title}>Bem-vindo de volta</p>
        <p className={classes.subtitle}>Entre na sua conta para continuar</p>

        <div className={classes.divider} />

        {error && <div className={classes.errorBox}>{error}</div>}

        {/* E-mail */}
        <div className={classes.fieldWrap}>
          <label className={classes.label}>E-mail</label>
          <div style={{ position: "relative" }}>
            <span className={classes.iconLeft}>
              <svg width="16" height="12" viewBox="0 0 17 13" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x=".85" y=".85" width="15.3" height="11.3" rx="2" />
                <path d="M.85 3l7.65 4.8L16.15 3" />
              </svg>
            </span>
            <TextField
              variant="outlined"
              fullWidth
              size="small"
              type="email"
              placeholder="seu@email.com.br"
              value={user.email}
              onChange={(e) => setUser({ ...user, email: e.target.value })}
              className={classes.textField}
              autoComplete="off"
            />
          </div>
        </div>

        {/* Senha */}
        <div className={classes.fieldWrap}>
          <label className={classes.label}>Senha</label>
          <div style={{ position: "relative" }}>
            <span className={classes.iconLeft}>
              <svg width="14" height="16" viewBox="0 0 15 17" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x=".85" y="7.35" width="13.3" height="9.3" rx="2" />
                <path d="M3.5 7V5a4 4 0 0 1 8 0v2" />
              </svg>
            </span>
            <TextField
              variant="outlined"
              fullWidth
              size="small"
              type={showPassword ? "text" : "password"}
              placeholder="••••••••"
              value={user.password}
              onChange={(e) => setUser({ ...user, password: e.target.value })}
              className={classes.textField}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      style={{ color: "rgba(160,140,210,.5)", padding: "6px" }}
                    >
                      {showPassword
                        ? <VisibilityOff style={{ fontSize: 17 }} />
                        : <Visibility style={{ fontSize: 17 }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>
        </div>

        {/* Lembrar */}
        <div
          className={classes.rememberRow}
          onClick={() => setUser({ ...user, remember: !user.remember })}
        >
          <div className={`${classes.toggle} ${user.remember ? classes.toggleOn : ""}`}>
            <div className={`${classes.toggleThumb} ${user.remember ? classes.toggleThumbOn : ""}`} />
          </div>
          <span className={classes.rememberLabel}>Lembrar de mim</span>
        </div>

        {/* Botão */}
        <Button
          type="button"
          variant="contained"
          disableElevation
          className={classes.submitBtn}
          onClick={handleSubmit}
        >
          Entrar
        </Button>

        {/* Links */}
        <div className={classes.links}>
          <RouterLink to="/forgot-password" className={classes.linkForgot}>
            Esqueceu a senha?
          </RouterLink>
          {userCreationEnabled && (
            <>
              <span className={classes.sep}>— ou —</span>
              <RouterLink to="/signup" className={classes.linkCreate}>
                Criar uma conta
              </RouterLink>
            </>
          )}
        </div>

        <p className={classes.footer}>© 2026 ChatPrime · Soluções Corporativas</p>
      </div>

      {/* WhatsApp fixo */}
      <div className={classes.whatsappBtn} onClick={() => window.open(branding.loginWhatsapp)}>
        <WhatsAppIcon style={{ fontSize: 26, color: "#07071a" }} />
      </div>
    </div>
  );
};

export default Login;
